import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineDelete } from "react-icons/md";
import { AlertDialogs } from "../Dialgos/Alter-Daylogs";
import EditCouponModel from "../Coupons/EditCouponModel";
import UAEDirhamIcon from "src/Components/utils/UAEDirhamIcon";
import { indexStore } from "src/store/indexStore";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { Card, CardContent, CardHeader } from "../ui/card";
import {
  Tag,
  Calendar,
  ShoppingCart,
  Gift,
  CheckCircle2,
  XCircle,
  Truck,
  Clock,
  TrendingUp,
  Sparkles,
  ShoppingBag,
} from "lucide-react";
import Visibility from "../utils/Visibility";

// Helper function to format date only (without time)
const formatDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const CouponCard = ({ element }) => {
  const {
    code,
    type,
    value,
    is_active,
    max_uses,
    used_count,
    remaining_uses,
    starts_at,
    expires_at,
    description,
    minimum_order_amount,
    is_first_register,
    is_free_delivery,

    take_away,
  } = element;

  const { t } = useTranslation();
  const { deleteCouponFunction } = indexStore();

  // Calculate validity progress and status
  const validityInfo = useMemo(() => {
    if (!starts_at || !expires_at) return null;

    const now = new Date();
    const start = new Date(starts_at);
    const end = new Date(expires_at);
    const total = end - start;
    const elapsed = now - start;
    const remaining = end - now;

    let status = "upcoming"; // upcoming, active, expired
    let progress = 0;
    let daysRemaining = 0;
    let statusColor = "bg-gray-500";
    let statusText = "";

    if (now < start) {
      status = "upcoming";
      progress = 0;
      daysRemaining = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
      statusColor = "bg-blue-500";
      statusText = t("gloabl.start") || "Starts";
    } else if (now >= start && now <= end) {
      status = "active";
      progress = Math.min(100, Math.max(0, (elapsed / total) * 100));
      daysRemaining = Math.ceil(remaining / (1000 * 60 * 60 * 24));
      statusColor = "bg-green-500";
      statusText = t("gloabl.active") || "Active";
    } else {
      status = "expired";
      progress = 100;
      daysRemaining = 0;
      statusColor = "bg-red-500";
      statusText = t("gloabl.expired") || "Expired";
    }

    return {
      status,
      progress,
      daysRemaining,
      statusColor,
      statusText,
      startDate: start,
      endDate: end,
    };
  }, [starts_at, expires_at, t]);

  // Calculate usage percentage
  const usagePercentage = useMemo(() => {
    if (!max_uses || max_uses === 0) return 0;
    return Math.min(100, (used_count / max_uses) * 100);
  }, [max_uses, used_count]);

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-2 hover:border-primary/20">
      {/* Gradient Header */}
      <div
        className={`relative bg-gradient-to-r ${
          is_active
            ? "from-green-500/10 via-blue-500/10 to-purple-500/10 dark:from-green-500/20 dark:via-blue-500/20 dark:to-purple-500/20"
            : "from-gray-500/10 to-gray-500/5 dark:from-gray-500/20 dark:to-gray-500/10"
        }`}
      >
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex-1 w-full sm:w-auto">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 rounded-lg bg-primary/10 dark:bg-primary/20">
                  <Tag className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-2xl sm:text-xl font-bold text-foreground tracking-tight">
                  {code}
                </h3>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* Discount Badge */}
                {type && value && (
                  <Badge
                    className={`text-sm font-semibold px-3 py-1 ${
                      type === "percentage"
                        ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0 shadow-md"
                        : "bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md"
                    }`}
                  >
                    {type === "percentage" ? (
                      `${value}%`
                    ) : (
                      <span className="flex items-center gap-1">
                        <UAEDirhamIcon className="w-3 h-3" />
                        {value}
                      </span>
                    )}
                    <span className="ml-1 opacity-90">
                      {t(`gloabl.${type}`)}
                    </span>
                  </Badge>
                )}

                {/* Free Delivery Badge */}
                {is_free_delivery && (
                  <Badge className="text-sm font-semibold px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 shadow-md">
                    {t("gloabl.free_delivery")}
                  </Badge>
                )}

                {/* Take Away Only Badge */}
                {take_away && (
                  <Badge className="text-sm font-semibold px-3 py-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 shadow-md flex items-center gap-1">
                    <ShoppingBag className="w-3 h-3" />
                    {t("gloabl.is_take_away_only")}
                  </Badge>
                )}

                {/* Status Badge */}
                <Badge
                  className={`text-xs font-semibold px-2 py-1 ${
                    is_active
                      ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300"
                      : "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300"
                  }`}
                >
                  {is_active ? (
                    <>
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {t("gloabl.active")}
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3 h-3 mr-1" />
                      {t("gloabl.inactive")}
                    </>
                  )}
                </Badge>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <EditCouponModel element={element} />
              <Visibility visible={element.used_count === 0}>
                <AlertDialogs
                  trigger={
                    <div className="p-2 rounded-lg hover:bg-destructive/10 transition-colors cursor-pointer">
                      <MdOutlineDelete className="text-xl text-destructive hover:text-destructive/80" />
                    </div>
                  }
                  variant="icon"
                  onClickOk={() => deleteCouponFunction(element.id)}
                  description={<span>{t("gloabl.deleteMessage")}</span>}
                  title={`${t("gloabl.delete")} ${t("gloabl.coupon")}`}
                />
              </Visibility>
            </div>
          </div>
        </CardHeader>
      </div>

      <CardContent className="space-y-5 p-6">
        {/* Validity Section with Progress Bar */}
        {validityInfo && (
          <div className="space-y-3 p-4 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border border-blue-200/50 dark:border-blue-800/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-blue-500/10 dark:bg-blue-500/20">
                  <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-sm font-semibold text-foreground">
                  {t("gloabl.validity")}
                </span>
              </div>
              <Badge
                className={`text-xs font-semibold ${validityInfo.statusColor} text-white`}
              >
                {validityInfo.statusText}
              </Badge>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{formatDateOnly(validityInfo.startDate)}</span>
                <span className="font-medium">
                  {validityInfo.daysRemaining > 0
                    ? `${validityInfo.daysRemaining} ${
                        validityInfo.daysRemaining === 1
                          ? t("gloabl.day") || "day"
                          : t("gloabl.days") || "days"
                      } ${
                        validityInfo.status === "upcoming"
                          ? t("gloabl.remaining") || "remaining"
                          : t("gloabl.left") || "left"
                      }`
                    : validityInfo.status === "expired"
                    ? t("gloabl.expired") || "Expired"
                    : t("gloabl.today") || "Today"}
                </span>
                <span>{formatDateOnly(validityInfo.endDate)}</span>
              </div>
              <div className="relative h-2.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`absolute top-0 left-0 h-full ${validityInfo.statusColor} transition-all duration-500 ease-out rounded-full`}
                  style={{ width: `${validityInfo.progress}%` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
              </div>
            </div>
          </div>
        )}

        {/* Usage Statistics with Progress */}
        <div className="flex flex-col  gap-3">
          <div className="p-4 flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/50 rounded-xl border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t("gloabl.max_uses")}
              </p>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold text-foreground">
              {max_uses || "∞"}
            </p>
          </div>
          <div className="p-4 flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/50 rounded-xl border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t("gloabl.used_count")}
              </p>
              <Clock className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-foreground">
                {used_count || 0}
              </p>
              {max_uses && max_uses > 0 && (
                <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-500 rounded-full"
                    style={{ width: `${usagePercentage}%` }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="p-4 flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/50 rounded-xl border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t("gloabl.remaining_uses")}
              </p>
              <Gift className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold text-foreground">
              {remaining_uses !== null && remaining_uses !== undefined
                ? remaining_uses
                : "∞"}
            </p>
          </div>
        </div>

        {/* Additional Details */}
        <div className="space-y-3 pt-3 border-t">
          {minimum_order_amount && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
              <div className="flex items-center gap-2 text-sm">
                <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {t("gloabl.minimum_order_amount")}
                </span>
              </div>
              <span className="flex items-center gap-1 font-bold text-lg text-foreground">
                <UAEDirhamIcon className="w-5 h-5" />
                {minimum_order_amount}
              </span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
            <div className="flex items-center gap-2 text-sm">
              <Gift className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {t("gloabl.is_first_register")}
              </span>
            </div>
            <Badge
              variant="outline"
              className={`font-semibold ${
                is_first_register
                  ? "bg-green-50 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700"
                  : "bg-gray-50 text-gray-500 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
              }`}
            >
              {is_first_register ? (
                <>
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {t("gloabl.yes_text")}
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3 mr-1" />
                  {t("gloabl.no_text")}
                </>
              )}
            </Badge>
          </div>
        </div>

        {/* Description */}
        {description && (
          <div className="pt-3 border-t">
            <p className="text-sm text-muted-foreground leading-relaxed italic bg-muted/30 p-3 rounded-lg">
              "{description}"
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CouponCard;

export const CouponCardSkeleton = () => {
  return (
    <div className="flex h-fit flex-col gap-3 rounded-lg border p-4 shadow-sm  animate-pulse">
      <div className="flex justify-between">
        <div>
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-14 mt-1" />
        </div>

        <div className="flex gap-2">
          <Skeleton className="w-6 h-6 rounded-md" />
          <Skeleton className="w-6 h-6 rounded-md" />
        </div>
      </div>

      <Skeleton className="w-16 h-4 rounded-md" />

      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>

      <div className="border-t pt-2 space-y-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
      </div>
    </div>
  );
};
